import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import ConsentimientoDatos from '../clinica/ConsentimientoDatos';
import { CONSENTIMIENTO_OBLIGATORIO, CONSENTIMIENTO_OPCIONAL, CONSENTIMIENTO_VERSION } from '../../config/consentimientoCuestionarios';
import {
  ChevronLeft, ChevronRight, Check, X, 
  ClipboardList, CheckCircle, AlertCircle, Loader2,
  FileText, Edit3, Shield
} from 'lucide-react';

// Import questionnaire schemas
import { SF12_SCHEMA, DOLOR_MEFIR_SCHEMA, VAS_OFICIAL_SCHEMA, QUESTION_TYPES, calculateSF12Score, calculateDolorScore, calculateVASScore } from '../admin/clinica/questionnaireSchemas';
import { ODI_SCHEMA, EFP0_SCHEMA, calculateODIScore, calculateEFP0Score } from '../admin/clinica/additionalQuestionnaires';
import { EQ5D5L_SCHEMA, calculateEQ5D5LScore } from '../admin/clinica/eq5d5lSchema';
import { SRS22R_SCHEMA, calculateSRS22RScore } from '../admin/clinica/srs22rSchema';
import { SAQ_SCHEMA, calculateSAQScore } from '../admin/clinica/saqSchema';
import { PSFS_SCHEMA, calculatePSFSScore } from '../admin/clinica/psfsSchema';

// Import specialized components
import VASScale from '../admin/clinica/VASScale';
import EQ5D5LQuestionnaire from '../admin/clinica/EQ5D5LQuestionnaire';
import SRS22RQuestionnaire from '../admin/clinica/SRS22RQuestionnaire';
import SAQQuestionnaire from '../admin/clinica/SAQQuestionnaire';
import PSFSQuestionnaire from '../admin/clinica/PSFSQuestionnaire';

// Map questionnaire IDs to their schemas
const QUESTIONNAIRE_SCHEMAS = {
  'sf12': SF12_SCHEMA,
  'dolor_mefir': DOLOR_MEFIR_SCHEMA,
  'odi': ODI_SCHEMA,
  'efp0': EFP0_SCHEMA,
  'vas_oficial': VAS_OFICIAL_SCHEMA,
  'eq5d5l': EQ5D5L_SCHEMA,
  'srs22r': SRS22R_SCHEMA,
  'saq': SAQ_SCHEMA,
  'psfs': PSFS_SCHEMA,
};

// Score calculators
const SCORE_CALCULATORS = {
  'sf12': calculateSF12Score,
  'dolor_mefir': calculateDolorScore,
  'odi': calculateODIScore,
  'efp0': calculateEFP0Score,
  'vas_oficial': calculateVASScore,
  'eq5d5l': calculateEQ5D5LScore,
  'srs22r': calculateSRS22RScore,
  'saq': calculateSAQScore,
  'psfs': calculatePSFSScore,
};

// FaceScale component for pain scale questions
const FaceScale = ({ value, onChange, min = 0, max = 10 }) => {
  const faces = [
    { value: 0, label: 'Sin dolor' },
    { value: 2, label: 'Leve' },
    { value: 4, label: 'Moderado-Leve' },
    { value: 6, label: 'Moderado' },
    { value: 8, label: 'Intenso' },
    { value: 10, label: 'Maximo' },
  ];

  const renderFace = (faceValue) => {
    const isSelected = value === faceValue;
    const baseColor = isSelected ? '#10b981' : '#94a3b8';
    
    return (
      <svg viewBox="0 0 100 100" className="w-12 h-12">
        <ellipse cx="50" cy="50" rx="40" ry="45" fill="none" stroke={baseColor} strokeWidth="3"/>
        <circle cx="35" cy="40" r="4" fill={baseColor}/>
        <circle cx="65" cy="40" r="4" fill={baseColor}/>
        
        {faceValue === 0 && (
          <path d="M 30 60 Q 50 80 70 60" fill="none" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
        )}
        {faceValue === 2 && (
          <path d="M 35 62 L 65 62" fill="none" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
        )}
        {faceValue === 4 && (
          <path d="M 35 65 L 65 65" fill="none" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
        )}
        {faceValue === 6 && (
          <path d="M 32 68 Q 50 62 68 68" fill="none" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
        )}
        {faceValue === 8 && (
          <path d="M 30 72 Q 50 60 70 72" fill="none" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
        )}
        {faceValue === 10 && (
          <>
            <path d="M 28 75 Q 50 58 72 75" fill="none" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
            <line x1="31" y1="36" x2="39" y2="44" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
            <line x1="39" y1="36" x2="31" y2="44" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
            <line x1="61" y1="36" x2="69" y2="44" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
            <line x1="69" y1="36" x2="61" y2="44" stroke={baseColor} strokeWidth="3" strokeLinecap="round"/>
          </>
        )}
      </svg>
    );
  };

  return (
    <div className="grid grid-cols-3 gap-3">
      {faces.map((face) => (
        <button
          key={face.value}
          onClick={() => onChange(face.value)}
          className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
            value === face.value 
              ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105' 
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          {renderFace(face.value)}
          <span className={`text-lg font-bold mt-2 ${value === face.value ? 'text-emerald-600' : 'text-gray-600'}`}>
            {face.value}
          </span>
          <span className={`text-xs text-center ${value === face.value ? 'text-emerald-700' : 'text-gray-500'}`}>
            {face.label}
          </span>
        </button>
      ))}
    </div>
  );
};

// Question renderer component
const QuestionRenderer = ({ question, value, onChange, onAutoAdvance }) => {
  // Un solo salto pendiente a la vez. Si se toca otra opción (cambio de
  // opinión) se anula el salto anterior en vez de dejar dos en marcha, que era
  // lo que hacía saltarse preguntas enteras (Ann, 22-jul-2026).
  const saltoRef = useRef(null);
  useEffect(() => () => clearTimeout(saltoRef.current), []);

  const handleSelectAndAdvance = (selectedValue) => {
    onChange(selectedValue);
    if (onAutoAdvance) {
      clearTimeout(saltoRef.current);
      saltoRef.current = setTimeout(() => onAutoAdvance(), 300);
    }
  };

  switch (question.type) {
    case QUESTION_TYPES.RADIO:
      return (
        <div className="space-y-3">
          {question.options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelectAndAdvance(option.value)}
              className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                value === option.value
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  value === option.value ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                }`}>
                  {value === option.value && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-base">{option.label}</span>
              </div>
            </button>
          ))}
        </div>
      );

    case QUESTION_TYPES.FACE_SCALE:
      return <FaceScale value={value} onChange={handleSelectAndAdvance} min={question.min} max={question.max} />;

    case QUESTION_TYPES.SCALE:
      return (
        <div className="space-y-4">
          <input
            type="range"
            min={question.min || 0}
            max={question.max || 10}
            value={value ?? question.min ?? 0}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
          <div className="flex justify-between text-sm text-gray-500">
            <span>{question.minLabel || question.min || 0}</span>
            <span className="text-2xl font-bold text-emerald-600">{value ?? '?'}</span>
            <span>{question.maxLabel || question.max || 10}</span>
          </div>
        </div>
      );

    case QUESTION_TYPES.NUMBER:
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
          min={question.min}
          max={question.max}
          className="w-full p-4 text-xl text-center border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-emerald-500"
          placeholder={question.placeholder || 'Introduce un numero'}
        />
      );

    case QUESTION_TYPES.TEXT:
    case QUESTION_TYPES.TEXTAREA:
      return (
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-emerald-500 resize-none"
          placeholder={question.placeholder || 'Escribe tu respuesta...'}
        />
      );

    default:
      return <div className="text-gray-500">Tipo de pregunta no soportado</div>;
  }
};

const PatientQuestionnaireWizard = ({ 
  cuestionarioId, 
  cuestionarioTipo, 
  cuestionarioNombre,
  onComplete, 
  onCancel 
}) => {
  // Step flow: consent -> signature -> questions -> complete
  const [step, setStep] = useState('consent');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [consentTimestamp, setConsentTimestamp] = useState(null);
  // Casilla OPCIONAL (uso anonimizado para investigación): NO bloquea el cuestionario,
  // pero se guarda marcada o sin marcar (prueba del consentimiento · RGPD).
  const [consentInvestigacion, setConsentInvestigacion] = useState(false);
  const [signatureTimestamp, setSignatureTimestamp] = useState(null);
  const sigCanvas = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [specialComponentData, setSpecialComponentData] = useState(null);
  const [vasValue, setVasValue] = useState(null); // For VAS special questionnaire
  // Aviso cuando se intenta terminar con preguntas sin contestar.
  const [faltanAviso, setFaltanAviso] = useState('');

  // Copias vivas de respuestas e índice. El avance automático va con un
  // temporizador de 300 ms: cuando salta, el `answers` y el índice que veía al
  // programarse ya son viejos. Con estas referencias se comprueba lo que hay
  // AHORA, no lo que había al tocar.
  const answersRef = useRef({});
  const indiceRef = useRef(0);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { indiceRef.current = currentQuestionIndex; }, [currentQuestionIndex]);

  /* RETOMAR DONDE LO DEJÓ (Ann, 28-ago-2026: «que el paciente, si no lo
     rellena, quede pendiente de rellenar, y al regresar y darle a completar le
     lleve a la pregunta pendiente y siguientes»).
     Antes se mandaba TODO de golpe al final: si cerraba a mitad se perdía
     entero y volvía a empezar. Y probablemente no lo repetía.
     Si lo empezado caducó (7 días), el servidor devuelve null y arranca limpio:
     mejor perder unas respuestas que guardar un dato que mezcla dos momentos. */
  const [retomado, setRetomado] = useState(false);
  useEffect(() => {
    if (!cuestionarioId) { setRetomado(true); return; }
    axios.get(`${API}/paciente/cuestionario/${cuestionarioId}/parcial`)
      .then((r) => {
        const p = r.data && r.data.parcial;
        if (p && p.respuestas) {
          setAnswers(p.respuestas);
          setCurrentQuestionIndex(Number(p.indice) || 0);
        }
      })
      .catch(() => { /* si no se puede leer, empieza de cero: nunca se inventa */ })
      .finally(() => setRetomado(true));
  }, [cuestionarioId]);

  /* GUARDA EN CADA RESPUESTA. No al final ni «cada X»: en cada una. Si se le
     acaba la batería o cierra sin querer, no pierde nada.
     `retomado` evita pisar lo guardado con el estado vacío del arranque. */
  useEffect(() => {
    if (!retomado || !cuestionarioId) return;
    if (!answers || Object.keys(answers).length === 0) return;
    axios.post(`${API}/paciente/cuestionario/${cuestionarioId}/parcial`,
      { respuestas: answers, indice: currentQuestionIndex })
      .catch(() => { /* en silencio: es una red de seguridad, no la vía principal */ });
  }, [answers, currentQuestionIndex, retomado, cuestionarioId]);

  const estaRespondida = (q) => {
    const v = answersRef.current[q?.id];
    return v !== undefined && v !== null && v !== '';
  };

  // Handle consent acceptance
  const handleConsentAccept = () => {
    setConsentAccepted(!consentAccepted);
    if (!consentAccepted) {
      setConsentTimestamp(new Date().toISOString());
    } else {
      setConsentTimestamp(null);
    }
  };

  // Handle signature clear
  const handleClearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignature(null);
    setSignatureTimestamp(null);
  };

  // Handle signature save
  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      setSignature(dataUrl);
      setSignatureTimestamp(new Date().toISOString());
      setStep('questions');
    } else {
      toast.error('Por favor, firme antes de continuar');
    }
  };

  const schema = QUESTIONNAIRE_SCHEMAS[cuestionarioTipo];

  // Check if questionnaire uses a special component
  const isSpecialQuestionnaire = ['vas_oficial', 'eq5d5l', 'srs22r', 'saq', 'psfs'].includes(cuestionarioTipo);

  // Get all questions from schema
  const getAllQuestions = () => {
    if (!schema || isSpecialQuestionnaire) return [];
    const questions = [];
    schema.sections?.forEach(section => {
      section.questions?.forEach(q => {
        questions.push({ ...q, sectionTitle: section.title });
      });
    });
    return questions;
  };

  const allQuestions = getAllQuestions();
  const currentQuestion = allQuestions[currentQuestionIndex];
  const progress = allQuestions.length > 0 ? ((currentQuestionIndex + 1) / allQuestions.length) * 100 : 0;

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    answersRef.current = { ...answersRef.current, [questionId]: value };
    if (faltanAviso) setFaltanAviso('');
  };

  const handleNext = () => {
    const i = indiceRef.current;
    // CANDADO · nunca se pasa de una pregunta sin responder.
    //
    // Al tocar una opción se programa un salto automático a 300 ms. Si la
    // paciente tocaba dos veces —o cambiaba de opinión y tocaba otra opción—
    // se programaban DOS saltos y se saltaba una pregunta entera, que se
    // quedaba sin contestar y sin avisar. Así llegó a producción un SF-12 con
    // 8 respuestas de 12 (Ann, 22-jul-2026), que ya no se puede puntuar.
    // Con esta guarda, el segundo salto se encuentra la pregunta siguiente sin
    // responder y no hace nada.
    if (!estaRespondida(allQuestions[i])) return;
    if (i < allQuestions.length - 1) {
      setCurrentQuestionIndex(i + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSpecialComplete = (data) => {
    setSpecialComponentData(data);
    handleFinishWithData(data.answers || data, data);
  };

  const handleFinish = () => {
    // CANDADO · un cuestionario clínico no se cierra con agujeros.
    //
    // Antes, «Finalizar» solo miraba la pregunta en la que estabas. Si alguna
    // anterior se había quedado sin contestar, se guardaba igual y marcada como
    // «completado»; luego la escala no se podía calcular y en la ficha salía un
    // 0 que parece una puntuación real. Ahora se vuelve a la primera que falta
    // y se dice cuántas son (Ann, 22-jul-2026).
    const sinResponder = allQuestions.filter(q => !estaRespondida(q));
    if (sinResponder.length > 0) {
      const primera = allQuestions.findIndex(q => q.id === sinResponder[0].id);
      setCurrentQuestionIndex(primera >= 0 ? primera : 0);
      setFaltanAviso(
        sinResponder.length === 1
          ? 'Falta 1 pregunta por contestar. Te llevamos a ella.'
          : `Faltan ${sinResponder.length} preguntas por contestar. Te llevamos a la primera.`
      );
      return;
    }

    // Calculate score
    const calculator = SCORE_CALCULATORS[cuestionarioTipo];
    let resultados = {};
    
    if (calculator) {
      try {
        resultados = calculator(answers);
      } catch (e) {
        console.error('Error calculating score:', e);
      }
    }

    handleFinishWithData(answers, resultados);
  };

  const handleFinishWithData = async (respuestas, resultados) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/paciente/cuestionario/${cuestionarioId}/responder`, {
        respuestas: respuestas,
        resultados: resultados,
        // Consent and signature data
        consentimiento: {
          aceptado: consentAccepted,
          fecha_hora: consentTimestamp,
          texto: CONSENTIMIENTO_OBLIGATORIO,
          version: CONSENTIMIENTO_VERSION,
          // Casilla OPCIONAL: se guarda SIEMPRE (true o false), nunca se omite.
          uso_investigacion_anonimizado: consentInvestigacion,
          texto_investigacion: CONSENTIMIENTO_OPCIONAL
        },
        firma: {
          imagen: signature,
          fecha_hora: signatureTimestamp
        }
      });
      
      setShowComplete(true);
      toast.success('Cuestionario completado correctamente');
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      /* SE DICE QUÉ HA PASADO, no «error» a secas. Ann perdió una mañana con un
         SF-12 que fallaba al final: el mensaje generico no decia ni el motivo
         ni --lo que mas angustia-- que sus respuestas SÍ estaban guardadas.
         El asistente guarda en CADA respuesta (`/parcial`), asi que aunque este
         ultimo paso falle, lo contestado NO se pierde y se puede retomar. */
      const detalle = error?.response?.data?.detail;
      toast.error(
        typeof detalle === 'string' && detalle
          ? `No se ha podido guardar: ${detalle}`
          : 'No se ha podido guardar el cuestionario.'
      );
      toast('Tus respuestas están guardadas. Puedes volver a entrar y continuar donde lo dejaste.',
        { duration: 8000, icon: '💾' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!schema) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Cuestionario no disponible</h2>
            <p className="text-gray-500 mb-4">Este tipo de cuestionario no esta disponible en este momento.</p>
            <Button onClick={onCancel}>Volver</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show completion screen
  if (showComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Completado!</h2>
            <p className="text-gray-600 mb-6">
              Gracias por completar el cuestionario. Tu terapeuta recibira tus respuestas.
            </p>
            <Button 
              onClick={onComplete}
              className="bg-emerald-500 hover:bg-emerald-600 w-full"
            >
              Volver a mi espacio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Consent Step
  if (step === 'consent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={onCancel} className="text-gray-600">
              <X className="w-5 h-5 mr-1" />
              Cancelar
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">{cuestionarioNombre}</h1>
            <div className="w-20" />
          </div>

          <Card className="shadow-lg">
            <CardHeader className="border-b bg-gray-50">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-emerald-600" />
                Consentimiento Informado
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Texto legal ÚNICO (config/consentimientoCuestionarios.js) · idéntico
                  en tablet, en consulta y aquí. No duplicar el texto en esta pantalla. */}
              <ConsentimientoDatos
                aceptado={consentAccepted}
                onAceptar={handleConsentAccept}
                investigacion={consentInvestigacion}
                onInvestigacion={setConsentInvestigacion}
                grande
              />

              <div className="flex gap-3 mt-8">
                <Button
                  variant="outline"
                  onClick={onCancel}
                  className="h-12"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={() => setStep('signature')}
                  disabled={!consentAccepted}
                  className={`flex-1 h-12 text-lg gap-2 ${
                    consentAccepted 
                      ? 'bg-emerald-500 hover:bg-emerald-600' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Continuar
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Signature Step
  if (step === 'signature') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={onCancel} className="text-gray-600">
              <X className="w-5 h-5 mr-1" />
              Cancelar
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">{cuestionarioNombre}</h1>
            <div className="w-20" />
          </div>

          <Card className="shadow-lg">
            <CardHeader className="border-b bg-gray-50">
              <CardTitle className="flex items-center gap-2">
                <Edit3 className="w-6 h-6 text-emerald-600" />
                Firma Digital
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 mb-4 text-center">
                Por favor, firme en el recuadro inferior para confirmar su consentimiento
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white mb-4">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'w-full',
                    style: { width: '100%', height: '200px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              
              <div className="flex gap-3 mb-6">
                <Button
                  variant="outline"
                  onClick={handleClearSignature}
                  className="flex-1"
                >
                  Borrar firma
                </Button>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep('consent')}
                  className="h-12"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Volver
                </Button>
                <Button
                  onClick={handleSaveSignature}
                  className="flex-1 h-12 text-lg bg-emerald-500 hover:bg-emerald-600 gap-2"
                >
                  Firmar y Continuar
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Special questionnaires with their own components
  // VAS needs special handling as it's a simple scale without submit button
  const handleVasSubmit = () => {
    if (vasValue !== null) {
      handleSpecialComplete({ vas_value: vasValue, score: vasValue });
    }
  };
  
  if (isSpecialQuestionnaire && step === 'questions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-6 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={onCancel} className="text-gray-600">
              <X className="w-5 h-5 mr-1" />
              Cancelar
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">{cuestionarioNombre}</h1>
            <div className="w-20" /> {/* Spacer */}
          </div>

          {/* Special Component */}
          <Card className="shadow-lg">
            <CardContent className="p-6">
              {cuestionarioTipo === 'vas_oficial' && (
                <div className="space-y-6">
                  <VASScale 
                    value={vasValue} 
                    onChange={setVasValue} 
                  />
                  <div className="flex justify-end pt-4 border-t">
                    <Button 
                      onClick={handleVasSubmit}
                      disabled={vasValue === null || submitting}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {submitting ? 'Guardando...' : 'Guardar respuesta'}
                    </Button>
                  </div>
                </div>
              )}
              {cuestionarioTipo === 'eq5d5l' && (
                <EQ5D5LQuestionnaire onComplete={handleSpecialComplete} />
              )}
              {cuestionarioTipo === 'srs22r' && (
                <SRS22RQuestionnaire onComplete={handleSpecialComplete} />
              )}
              {cuestionarioTipo === 'saq' && (
                <SAQQuestionnaire onComplete={handleSpecialComplete} />
              )}
              {cuestionarioTipo === 'psfs' && (
                <PSFSQuestionnaire onComplete={handleSpecialComplete} baseline={null} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Standard questionnaire wizard
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={onCancel} className="text-gray-600">
            <X className="w-5 h-5 mr-1" />
            Cancelar
          </Button>
          <span className="text-sm text-gray-500">
            {currentQuestionIndex + 1} de {allQuestions.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-gray-200 rounded-full mb-6">
          <div 
            className="h-full bg-emerald-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Question Card */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            {/* Section title */}
            {currentQuestion?.sectionTitle && (
              <p className="text-sm text-emerald-600 font-medium mb-2">
                {currentQuestion.sectionTitle}
              </p>
            )}

            {/* Question text */}
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {currentQuestion?.text}
            </h2>

            {/* Aviso visible (no un toast que se va): faltan preguntas. */}
            {faltanAviso && (
              <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 10,
                background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
                fontSize: 14, fontWeight: 600 }}>
                {faltanAviso}
              </div>
            )}

            {/* Question input */}
            {currentQuestion && (
              <QuestionRenderer
                question={currentQuestion}
                value={answers[currentQuestion.id]}
                onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                onAutoAdvance={currentQuestion.type === QUESTION_TYPES.RADIO ? handleNext : null}
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>

          {currentQuestionIndex === allQuestions.length - 1 ? (
            <Button
              onClick={handleFinish}
              disabled={submitting || answers[currentQuestion?.id] === undefined || answers[currentQuestion?.id] === null || answers[currentQuestion?.id] === ''}
              className={`gap-2 ${
                submitting || answers[currentQuestion?.id] === undefined || answers[currentQuestion?.id] === null || answers[currentQuestion?.id] === ''
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Finalizar
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={answers[currentQuestion?.id] === undefined || answers[currentQuestion?.id] === null || answers[currentQuestion?.id] === ''}
              className={`gap-2 ${
                answers[currentQuestion?.id] === undefined || answers[currentQuestion?.id] === null || answers[currentQuestion?.id] === ''
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientQuestionnaireWizard;
